import {Component, DestroyRef, inject, Inject, OnInit, SimpleChanges, OnChanges} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup} from '@angular/forms';
import {catchError, map, take} from 'rxjs/operators';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {MenuService} from '@emm/core/services/menu.service';
import {MenuStoreService} from '@emm/menu/services/menu-store.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {MenuFormFacadeService} from '@emm/menu/services/menu-form-facade.service';
import {BehaviorSubject, forkJoin, of} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'emm-update-item-dialog',
    styleUrls: ['./update-item-dialog.component.scss'],
    templateUrl: './update-item-dialog.component.html',
    standalone: false
})
export class UpdateItemDialogComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    featureScope: string;
    menuKey: MenuKeyContext;
    response;
    path;
    updates = [];
    currentSubmenu;
    locationSet = this.data.locationSet;
    effectiveDate = this.data.effectiveDate;
    removedMessage = 'Removed';
    itemDetail: ItemDetail;
    formattedOverrides = [];
    dataSource = new BehaviorSubject<any[]>([]);
    displayedColumns: string[] = [];
    forceOverridesInheritanceMap: Record<string, boolean> = {};
    isLoading = new BehaviorSubject<boolean>(true);

    constructor(
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<UpdateItemDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private menuService: MenuService,
        private menuStore: MenuStoreService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private api: MenuApiService,
        private formFacade: MenuFormFacadeService,
    ) {
        this.updates = data.updates;
        this.itemDetail = data.itemDetail;
        this.menuKey = data.menuKey;
        this.featureScope = this.data.featureScope;
    }


    ngOnInit(): void {
        this.menuStore.querySubmenus.selected$.subscribe(result => this.currentSubmenu = result);
        this.setDisplayedColumns();
        this.formatDataSource();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['locationSet']) {
            this.setDisplayedColumns();
        }
    }

    setDisplayedColumns(): void {
        this.displayedColumns = ['attribute', 'locations', 'currentValue', 'newValue'];
        if (this.locationSet !== 'Location') {
            this.displayedColumns.push('forceOverrides');
        }
    }

    forceOverrideForAttribute(row: any) {
        row.forceOverrides = !row?.forceOverrides;
        if (row?.forceOverrides) {
            this.forceOverridesInheritanceMap[row.attribute] = true;
        } else {
            delete this.forceOverridesInheritanceMap[row.attribute];
        }
    }

    onSave(): void {
        if (!this.currentSubmenu) {
            this.currentSubmenu = this.getSearchSubmenu();
        }
        this.formFacade.updateItem(this.itemDetail, this.updates, true, false,
            this.forceOverridesInheritanceMap);
        this.dialogRef.close({path: this.path, isChecked: false});
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    getSearchSubmenu(): string {
        let submenu = null;
        this.menuStore.querySubmenus.selectedSearchSubmenu$.pipe(take(1)).subscribe(value =>
            submenu = value);
        return submenu;
    }

    formatDataSource() {
        if (!this.itemDetail?.tag) {
            this.dataSource.next([]);
            this.isLoading.next(false);
            return;
        }
        const updateKeys = Object.keys(this.updates).filter(key => key !== 'components');
        if (updateKeys.length === 0) {
            this.dataSource.next([]);
            this.isLoading.next(false);
            return;
        }
        const tagPathArray = this.itemDetail?.tagPath?.split('/');
        tagPathArray?.shift();
        tagPathArray?.shift();
        const itemPath = tagPathArray?.join('/');

        const requests = updateKeys.map(attribute =>
            this.api.getAttributeOverrides(attribute, this.locationSet, this.currentSubmenu, `/${itemPath}`,
                this.effectiveDate).pipe(
                    takeUntilDestroyed(this.destroyRef),
                    map(res => ({attribute, res})),
                    catchError(() => of({attribute, res: null}))
                )
        );

        forkJoin(requests).subscribe({
            next: (results) => {
                this.formattedOverrides = [];
                const tableRows = results.map(({ attribute, res }) => {
                    const overrides = res
                        ? Object.entries(res.overrides).map(([key, value]) => ({ key, value }))
                        : [];

                    this.formattedOverrides.push(...overrides);

                    return {
                        attribute,
                        locations: overrides.length ? overrides.map(o => o.key).join(',\n') : 'None',
                        currentValue: this.itemDetail?.attributes.find(a => a.jsonProperty === attribute)?.value,
                        newValue: this.updates[attribute],
                        forceOverrides: false
                    };
                });

                this.dataSource.next(tableRows);
                this.isLoading.next(false);
            },
            error: (err) => {
                console.error('Error loading attribute overrides:', err);
                this.notificationService.error('Failed to load attribute overrides. Please try again.');
                this.isLoading.next(false);
            }
        });
    }
}
